import PropTypes from 'prop-types';
import React from 'react';
import CrmAvatar from '../../shared/components/CrmAvatar';
import CrmTableStateRow from '../../shared/components/CrmTableStateRow';
import RowActionsMenu from './RowActionsMenu';
import RowActionButtons from './RowActionButtons';
import { formatDateLine, formatEventTimes, formatGross, formatSupportNames } from '../utils/format';
import { CapacityMeter } from '@opendate-app/setlist';

const SortChevron = ({ direction }) => (
  <svg className="tickets-sort-chevron" viewBox="0 0 24 24" aria-hidden="true">
    {direction === 'asc'
      ? <path d="M7 14l5-5 5 5" />
      : <path d="M7 10l5 5 5-5" />}
  </svg>
);

SortChevron.propTypes = { direction: PropTypes.oneOf(['asc', 'desc']).isRequired };

const renderHeader = (col, { sortDirection, onSort }) => {
  switch (col.id) {
    case 'date':
      return (
        <th
          key={col.id}
          className="tickets-sortable"
          aria-sort={sortDirection === 'asc' ? 'ascending' : 'descending'}
        >
          <button type="button" className="tickets-sort-btn" onClick={onSort}>
            {col.label}
            <SortChevron direction={sortDirection} />
          </button>
        </th>
      );
    case 'gross':
      return <th key={col.id} className="text-right">{col.label}</th>;
    case 'artist_guarantee':
      return <th key={col.id} className="text-right">{col.label}</th>;
    case 'actions':
      return <th key={col.id} className="tickets-col-actions" aria-label="Actions" />;
    default:
      return <th key={col.id}>{col.label}</th>;
  }
};

const renderCell = (col, event, { team, tz }) => {
  switch (col.id) {
    case 'event': {
      const support = formatSupportNames(event.support_names);
      return (
        <td key={col.id}>
          <div className="tickets-event-cell">
            <CrmAvatar
              avatarUrl={event.headliner_image_url}
              name={event.headliner_name || event.event_name}
              size={36}
            />
            <div className="tickets-event-info">
              <span className="tickets-has-tip tickets-name-tip" data-tip="View event details">
                <a href={`/teams/${team.id}/confirms/${event.id}`} className="tickets-row-link">
                  {event.event_name}
                </a>
              </span>
              {support && (
                <div className="tickets-support-line" title={support}>{support}</div>
              )}
            </div>
          </div>
          <div className="tickets-event-actions">
            <RowActionButtons team={team} event={event} />
          </div>
        </td>
      );
    }
    case 'date': {
      const dateLine = formatDateLine(event.event_date, tz);
      const eventTimes = formatEventTimes(event, tz);
      return (
        <td key={col.id}>
          <div className="tickets-date-primary">{dateLine}</div>
          {eventTimes && (
            <div className="tickets-date-secondary">{eventTimes}</div>
          )}
        </td>
      );
    }
    case 'venue':
      return (
        <td key={col.id} className="tickets-venue-cell">
          {event.venue_name || '—'}
        </td>
      );
    case 'age_restriction':
      return <td key={col.id}>{event.age_restriction || '—'}</td>;
    case 'tickets_sold':
      return (
        <td key={col.id}>
          <CapacityMeter
            size="sm"
            label="none"
            unit="tickets"
            taken={event.tickets_sold || 0}
            capacity={event.venue_capacity || 0}
          />
        </td>
      );
    case 'gross':
      return (
        <td key={col.id} className="tickets-gross-cell">
          {formatGross(event.tickets_gross || 0)}
        </td>
      );
    case 'artist_guarantee':
      return (
        <td key={col.id} className="tickets-gross-cell">
          {formatGross(event.artist_guarantee || 0)}
        </td>
      );
    case 'actions':
      return (
        <td key={col.id} className="tickets-cell-actions">
          <div className="tickets-row-actions">
            <RowActionsMenu team={team} event={event} />
          </div>
        </td>
      );
    default:
      return <td key={col.id} />;
  }
};

const TicketsTable = ({
  events, loading, error, activeTab, timeZone, team, sortDirection, onSort, visibleColumns,
}) => {
  const isEmpty = !loading && !error && events.length === 0;
  const emptyMessage = `No events ${activeTab === 'on_sale_now' ? 'are currently on sale' : 'to show'}.`;

  return (
    <div className="tickets-table-card">
      <table className="tickets-table">
        <thead>
          <tr>
            {visibleColumns.map((col) => renderHeader(col, { sortDirection, onSort }))}
          </tr>
        </thead>
        <tbody>
          <CrmTableStateRow
            loading={loading}
            error={error}
            isEmpty={isEmpty}
            colSpan={visibleColumns.length}
            loadingMessage="Loading…"
            emptyMessage={emptyMessage}
          />
          {!loading && !error && events.map((event) => {
            const tz = event.venue_time_zone || timeZone;
            return (
              <tr key={event.id}>
                {visibleColumns.map((col) => renderCell(col, event, { team, tz }))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

TicketsTable.propTypes = {
  events: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  activeTab: PropTypes.string,
  timeZone: PropTypes.string,
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSort: PropTypes.func.isRequired,
  visibleColumns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
  })).isRequired,
};

export default TicketsTable;
